import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from "@mui/material"
import { If, Switch, Case, CaseElse } from "lib/@components/switch"
import Iconify from "minimals-template/components/Iconify"
import { getCurrencySymbol } from "../format-utils"
import { navigate } from "lib/routes/navigate"
import { isEnabled } from "lib/@components/feature"
import { rateCard } from "routes/rate-cards/controller/rate-card"
import { FEATURE_FLAG_SCHEDULE_ASSIGNMENTS } from "../../feature-flag-schedule-assignments"
import { scenarioSkillMapPath } from "./scenario-skill-map-path"
import { recalculateScenario } from "routes/scenarios/controller/recalculate-scenario"
import { recalculateVariant } from "routes/scenarios/controller/recalculate-variant"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { CostTotalCard } from "./cost-total-card"
import { CostFacilitiesCard } from "./cost-facilities-card"
import { CostCriticalityCard } from "./cost-criticality-card"
import { CostVisitSnapshotCard } from "./cost-visit-snapshot-card"
import { CostResourceTable } from "./cost-resource-table"
import { CostFixedContractsSection } from "./cost-fixed-contracts-section"
import { CostModifiersSection } from "./cost-modifiers-section"
import { CostTimeModifiersSection } from "./cost-time-modifiers-section"
import { CostFrequencyModifiersSection } from "./cost-frequency-modifiers-section"
import { CostSummaryFooter } from "./cost-summary-footer"
import { DataQualityAlerts } from "./data-quality-alerts"
import { Bound } from "lib/@components/binding/Bound"
import { ScenarioCostSummaryHeader } from "./scenario-cost-summary-header"

export function ScenarioCostSummary({
    data,
    canManageOptimiser,
    onEditScenario,
    activeVariantName,
    variantCount,
    onSwitchVariant,
}) {
    const status = data?.status
    const costResult = data?.costResult
    const currencySymbol = getCurrencySymbol(costResult?.currency)
    const dataQualityRecalculate = getDataQualityRecalculate()
    // Arguments the database-backed detail queries need; absent for legacy results
    // (no detailsRef), which keep rendering their inline arrays as before.
    const detailArgs = getDetailArgs()
    const isLegacyDetail = !!costResult && !costResult.detailsRef && !costResult.error
    // A failed recalculation preserves the last successful result, so the figures
    // stay usable; surface the failure as a warning over them rather than replacing
    // the whole summary with a generic error card.
    const hasUsableResult = !!costResult && !costResult.error
    const lastCalculationError = data?.lastCalculationError

    return (
        <Switch value={getViewState()}>
            <Case when="initial-calculation">
                <Card>
                    <CardContent>
                        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress />
                            <Typography variant="body2" color="text.secondary">
                                Calculating costs...
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            </Case>
            <Case when="error">
                <Card>
                    <CardContent>
                        <Stack spacing={2}>
                            <Alert severity="error">
                                {costResult?.error || "An error occurred during calculation"}
                            </Alert>
                            <If value={canManageOptimiser}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Iconify icon="mdi:refresh" />}
                                    onClick={handleRecalculate}
                                >
                                    Recalculate
                                </Button>
                            </If>
                        </Stack>
                    </CardContent>
                </Card>
            </Case>
            <Case when="cost-result">
                <If value={costResult}>
                    <Stack spacing={2}>
                        <If value={status === "calculating"}>
                            <Alert severity="info" icon={<CircularProgress size={20} />}>
                                Calculating costs... Current figures will remain visible until the updated result is
                                ready.
                            </Alert>
                        </If>
                        <ScenarioCostSummaryHeader
                            costResult={costResult}
                            activeVariantName={activeVariantName}
                            variantCount={variantCount}
                            onSwitchVariant={onSwitchVariant}
                        />

                        <If value={!!lastCalculationError}>
                            <Alert
                                severity="warning"
                                data-cy="stale-after-failed-recalculation-alert"
                                action={
                                    <If value={canManageOptimiser}>
                                        <Button color="inherit" size="small" onClick={handleRecalculate}>
                                            Retry
                                        </Button>
                                    </If>
                                }
                            >
                                The last recalculation failed ({lastCalculationError?.message}). These figures are from
                                the previous successful calculation.
                            </Alert>
                        </If>

                        <If value={isLegacyDetail}>
                            <Alert severity="info" data-cy="legacy-detail-alert">
                                Recalculate this scenario to generate the full detailed breakdown. Detail shown here may
                                be incomplete for large regimes.
                            </Alert>
                        </If>

                        <DataQualityAlerts
                            data={data}
                            costResult={costResult}
                            currencySymbol={currencySymbol}
                            regimeId={data?.regimeId}
                            onNavigateToRateCard={handleNavigateToRateCard}
                            onNavigateToSkillMapper={handleNavigateToSkillMapper}
                            onRecalculate={dataQualityRecalculate}
                        />

                        <If value={!isZeroTaskScenario()}>
                            <Stack direction="row" spacing={2}>
                                <CostTotalCard costResult={costResult} currencySymbol={currencySymbol} />
                                <CostFacilitiesCard
                                    facilityBreakdown={costResult?.facilityBreakdown}
                                    currencySymbol={currencySymbol}
                                    costResult={costResult}
                                    scenarioName={data?.name}
                                    regimeName={data?.regimeName}
                                    onEditScenario={onEditScenario}
                                    detailArgs={detailArgs}
                                />
                            </Stack>

                            <Stack direction="row" spacing={2}>
                                <CostCriticalityCard costResult={costResult} currencySymbol={currencySymbol} />
                                <CostVisitSnapshotCard costResult={costResult} currencySymbol={currencySymbol} />
                            </Stack>

                            <CostResourceTable
                                costResult={costResult}
                                currencySymbol={currencySymbol}
                                scenarioId={data?.id}
                                variantId={data?._variantId}
                            />
                            <CostFixedContractsSection costResult={costResult} currencySymbol={currencySymbol} />
                            <Bound
                                rateCardModifiers={data?.rateCardModifiers}
                                disabledModifierIds={data?.disabledModifierIds}
                                modifiersUpdating={status === "calculating"}
                                hasPendingModifierChanges={data?.hasPendingModifierChanges}
                            >
                                <CostModifiersSection costResult={costResult} currencySymbol={currencySymbol} />
                            </Bound>
                            <CostTimeModifiersSection costResult={costResult} />
                            <CostFrequencyModifiersSection costResult={costResult} />
                            <CostSummaryFooter costResult={costResult} currencySymbol={currencySymbol} />
                        </If>
                    </Stack>
                </If>
            </Case>
            <CaseElse>
                <Card>
                    <CardContent>
                        <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Iconify icon="mdi:chart-timeline-variant-shimmer" sx={{ fontSize: 48, opacity: 0.3 }} />
                            <Typography variant="body2" color="text.secondary">
                                Cost calculation has not been run yet.
                            </Typography>
                            <If value={canManageOptimiser}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Iconify icon="mdi:calculator" />}
                                    onClick={handleRecalculate}
                                >
                                    Calculate Costs
                                </Button>
                            </If>
                        </Box>
                    </CardContent>
                </Card>
            </CaseElse>
        </Switch>
    )

    function handleNavigateToRateCard() {
        if (data?.rateCardId) navigate(`/app/optimiser/rate-cards/${data.rateCardId}`)
    }

    // FIQ-2457: with schedule assignments on, open this scenario's own skill map with its
    // regime ready to browse; otherwise (or if the rate card has no skill map, or can't be
    // read) the list, so the button always goes somewhere
    async function handleNavigateToSkillMapper() {
        const skillMapId = await scenarioSkillMapId()
        if (skillMapId) {
            navigate(scenarioSkillMapPath({ skillMapId, regimeId: data.regimeId }))
            return
        }
        navigate("/app/optimiser?optimiserTab=skill-mappings")
    }

    async function scenarioSkillMapId() {
        if (!isEnabled(FEATURE_FLAG_SCHEDULE_ASSIGNMENTS) || !data?.rateCardId) return null
        try {
            const card = await rateCard({ id: data.rateCardId })
            return card?.skillMapId ?? null
        } catch (error) {
            errorSnackbar(`Couldn't open this scenario's skill map: ${error.message}`)
            return null
        }
    }

    function isZeroTaskScenario() {
        return costResult && costResult.taskCount === 0
    }

    function getDataQualityRecalculate() {
        if (!canManageOptimiser) return undefined
        return handleRecalculate
    }

    function getDetailArgs() {
        const setKey = costResult?.detailsRef?.setKey
        if (!setKey || !data?.id || !data?._variantId) return undefined
        return { scenarioId: data.id, variantId: data._variantId, setKey }
    }

    function getViewState() {
        // Only fall back to the error card when there is nothing usable to show. A
        // preserved result must not be hidden behind the failed recalculation.
        if (status === "error" && !hasUsableResult) return "error"
        if (costResult) return "cost-result"
        if (status === "calculating") return "initial-calculation"
        return status
    }

    async function handleRecalculate() {
        if (data._variantId) {
            await recalculateVariant({ scenarioId: data.id, variantId: data._variantId })
        } else {
            await recalculateScenario({ id: data.id })
        }
        successSnackbar("Recalculation started")
    }
}
